/**
 * Weekday multi-selection component for custom-weekday shift recurrence.
 *
 * Renders a seven-button toggle group (Mon..Sun) and emits the selection as an
 * ascending list of ISO weekday numbers (1=Mon .. 7=Sun), matching the wire
 * format used by the shift API and `shift_patterns.weekdays`.
 */

import { useLingui } from '@lingui/react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

export interface WeekdayMultiSelectProps {
  /** Currently selected ISO weekdays (1=Mon .. 7=Sun). */
  value: number[];
  /** Called with the new ascending ISO weekday selection. */
  onChange: (value: number[]) => void;
  disabled?: boolean;
  id?: string;
  'aria-describedby'?: string;
}

/**
 * A toggle-group for picking an arbitrary subset of weekdays.
 * Radix ToggleGroup operates on string values, so ISO numbers are mapped to/from
 * their string representation at the boundary.
 */
export function WeekdayMultiSelect({
  value,
  onChange,
  disabled,
  id,
  'aria-describedby': ariaDescribedBy,
}: WeekdayMultiSelectProps) {
  const { i18n } = useLingui();

  // ISO 1=Mon .. 7=Sun, rendered in that order.
  const weekdays: { iso: number; short: string; full: string }[] = [
    { iso: 1, short: i18n._({ id: 'weekday.mon.short', message: 'Mo' }), full: i18n._({ id: 'weekday.mon', message: 'Monday' }) },
    { iso: 2, short: i18n._({ id: 'weekday.tue.short', message: 'Tu' }), full: i18n._({ id: 'weekday.tue', message: 'Tuesday' }) },
    { iso: 3, short: i18n._({ id: 'weekday.wed.short', message: 'We' }), full: i18n._({ id: 'weekday.wed', message: 'Wednesday' }) },
    { iso: 4, short: i18n._({ id: 'weekday.thu.short', message: 'Th' }), full: i18n._({ id: 'weekday.thu', message: 'Thursday' }) },
    { iso: 5, short: i18n._({ id: 'weekday.fri.short', message: 'Fr' }), full: i18n._({ id: 'weekday.fri', message: 'Friday' }) },
    { iso: 6, short: i18n._({ id: 'weekday.sat.short', message: 'Sa' }), full: i18n._({ id: 'weekday.sat', message: 'Saturday' }) },
    { iso: 7, short: i18n._({ id: 'weekday.sun.short', message: 'Su' }), full: i18n._({ id: 'weekday.sun', message: 'Sunday' }) },
  ];

  const handleValueChange = (next: string[]) => {
    const parsed = next
      .map((v) => Number.parseInt(v, 10))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);
    onChange(parsed);
  };

  return (
    <ToggleGroup
      type="multiple"
      variant="outline"
      value={value.map(String)}
      onValueChange={handleValueChange}
      disabled={disabled}
      id={id}
      aria-describedby={ariaDescribedBy}
      className="flex-wrap justify-start gap-1"
    >
      {weekdays.map((day) => (
        <ToggleGroupItem
          key={day.iso}
          value={String(day.iso)}
          aria-label={day.full}
          className="w-10"
        >
          {day.short}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
